import {
  daysBetween,
  localDay,
  type ReportShipmentRow,
} from './build-shipments-section.ts';
import type {
  PendingSettlement,
  PendingSettlementsByVesselData,
  PendingSettlementsVessel,
  PendingSettlementStatus,
  PendingSettlementsListData,
  PendingSettlementsSummaryData,
  ShipmentReportBucket,
} from './types.ts';

// Liquidaciones por recibir, con las reglas de la pantalla de Despachos: un
// embarque es una referencia, está liquidado si tiene una liquidación aprobada
// y vence a los 30 días de su ETA declarada. Sobre eso, lo acordado para el
// reporte: críticas con más de 45 días desde la ETA, vencidas desde el día 30
// hasta el 45, y por vencer las que vencen en los próximos 7 días. Las que
// están en plazo y las ya liquidadas no entran.

export const SETTLEMENT_TERM_DAYS = 30;
const CRITICAL_AFTER_DAYS = 45;
const DUE_SOON_DAYS = 7;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

// Despachos la marca vencida desde el día 30, así que "por vencer" son los
// días 23 a 29: vence dentro de 1 a 7 días.
export const pendingSettlementStatus = (
  daysSinceEta: number
): PendingSettlementStatus | null => {
  if (daysSinceEta > CRITICAL_AFTER_DAYS) return 'critical';
  if (daysSinceEta >= SETTLEMENT_TERM_DAYS) return 'overdue';
  if (daysSinceEta >= SETTLEMENT_TERM_DAYS - DUE_SOON_DAYS) return 'dueSoon';
  return null;
};

const addDays = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * MILLISECONDS_PER_DAY)
    .toISOString()
    .slice(0, 10);

export type PendingSettlementsInput = {
  // Embarques ya zarpados de los clientes del reporte.
  rows: ReportShipmentRow[];
  // Referencias con liquidación aprobada, recortadas igual que en Despachos.
  settledReferences: Set<string>;
  today: Date;
};

const collect = ({ rows, settledReferences, today }: PendingSettlementsInput) => {
  const todayKey = localDay(today);
  const pending: PendingSettlement[] = [];
  let withoutEta = 0;
  for (const row of rows) {
    if (settledReferences.has(row.reference)) continue;
    if (!row.plannedEta) {
      withoutEta += 1;
      continue;
    }
    const daysSinceEta = daysBetween(row.plannedEta, todayKey);
    const status = pendingSettlementStatus(daysSinceEta);
    if (!status) continue;
    pending.push({
      reference: row.reference,
      identifiers: row.identifiers,
      vessel: row.vessel,
      transport: row.transport,
      destination: row.destination,
      eta: row.plannedEta,
      dueDate: addDays(row.plannedEta, SETTLEMENT_TERM_DAYS),
      daysSinceEta,
      status,
      ceq: row.ceq,
    });
  }
  return { pending, withoutEta };
};

const sum = (rows: PendingSettlement[]): ShipmentReportBucket => ({
  shipments: rows.length,
  ceq: rows.reduce((total, row) => total + row.ceq, 0),
});

const toList = (rows: PendingSettlement[]): PendingSettlementsListData => ({
  termDays: SETTLEMENT_TERM_DAYS,
  totals: sum(rows),
  rows,
});

export const buildPendingSettlementsSummary = (
  input: PendingSettlementsInput
): PendingSettlementsSummaryData => {
  const { pending, withoutEta } = collect(input);
  const bucket = (status: PendingSettlementStatus) =>
    sum(pending.filter((row) => row.status === status));
  return {
    termDays: SETTLEMENT_TERM_DAYS,
    critical: bucket('critical'),
    overdue: bucket('overdue'),
    dueSoon: bucket('dueSoon'),
    withoutEta,
  };
};

// Críticas y vencidas, de la más antigua a la más reciente.
export const buildOverdueSettlements = (input: PendingSettlementsInput) =>
  toList(
    collect(input)
      .pending.filter((row) => row.status !== 'dueSoon')
      .sort(
        (a, b) =>
          b.daysSinceEta - a.daysSinceEta || a.reference.localeCompare(b.reference)
      )
  );

// Por vencer, de la que vence primero a la última.
export const buildSettlementsDueSoon = (input: PendingSettlementsInput) =>
  toList(
    collect(input)
      .pending.filter((row) => row.status === 'dueSoon')
      .sort(
        (a, b) =>
          a.dueDate.localeCompare(b.dueDate) || a.reference.localeCompare(b.reference)
      )
  );

const STATUS_RANK: Record<PendingSettlementStatus, number> = { critical: 0, overdue: 1, dueSoon: 2 };

// Todo lo sin liquidar (críticas, vencidas y por vencer) por nave o vuelo y
// ETA: el cliente piensa por nave. Primero las más atrasadas.
export const buildSettlementsByVessel = (
  input: PendingSettlementsInput
): PendingSettlementsByVesselData => {
  const { pending } = collect(input);
  const groups = new Map<string, PendingSettlementsVessel>();
  for (const row of pending) {
    const key = [row.transport ?? '', row.vessel ?? '', row.eta].join('|');
    const group =
      groups.get(key) ??
      ({
        vessel: row.vessel,
        transport: row.transport,
        eta: row.eta,
        destinations: [],
        status: row.status,
        daysSinceEta: row.daysSinceEta,
        shipments: 0,
        ceq: 0,
        byStatus: { critical: 0, overdue: 0, dueSoon: 0 },
      } satisfies PendingSettlementsVessel);
    group.shipments += 1;
    group.ceq += row.ceq;
    group.byStatus[row.status] += 1;
    if (STATUS_RANK[row.status] < STATUS_RANK[group.status]) group.status = row.status;
    group.daysSinceEta = Math.max(group.daysSinceEta, row.daysSinceEta);
    if (row.destination && !group.destinations.includes(row.destination)) {
      group.destinations.push(row.destination);
    }
    groups.set(key, group);
  }
  const rows = Array.from(groups.values()).sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      b.daysSinceEta - a.daysSinceEta ||
      (a.vessel ?? '').localeCompare(b.vessel ?? '')
  );
  return { termDays: SETTLEMENT_TERM_DAYS, totals: sum(pending), rows };
};
